import React, { useEffect, useState } from 'react';
import api from '../api';
import Timeline from '../components/Timeline';
import { Pencil, Upload, Brain, Clock } from 'lucide-react';
import { ChatBubbleLeftRightIcon, DocumentTextIcon, SparklesIcon } from '@heroicons/react/24/outline';

const Topics = () => {
    const [topics, setTopics] = useState([]);
    const [newName, setNewName] = useState('');
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [swapA, setSwapA] = useState('');
    const [swapB, setSwapB] = useState('');
    const [showDeletePrompt, setShowDeletePrompt] = useState(false);
    const [pendingRenameId, setPendingRenameId] = useState(null);
    const [pendingNewName, setPendingNewName] = useState('');
    const [timelineItems, setTimelineItems] = useState([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    const [viewerItem, setViewerItem] = useState(null);
    const [viewerType, setViewerType] = useState('summary');
    const [viewerContent, setViewerContent] = useState('');
    const [removeItem, setRemoveItem] = useState(null);
    const [flippedCardId, setFlippedCardId] = useState(null);

    // Helper for number to word
    const numberWords = ['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'];

    useEffect(() => {
        api.get('/topics').then(res => setTopics(res.data));
    }, []);

    const renameTopic = (id, newName, deleteChats) => {
        api.patch(`/topics/${id}`, { new_topic_name: newName, deleteChats })
            .then(() => {
                setTopics(topics.map(t => t.topic_id === id ? { ...t, topic_name: newName } : t));
                setNewName('');
                setSelectedTopic(null);
            })
            .catch(err => alert('Error renaming topic'));
    };

    const swapTopics = () => {
        if (!swapA || !swapB || swapA === swapB) {
            alert('Please select two different topics.');
            return;
        }

        api.post('/topics/swap', { topic_a: parseInt(swapA), topic_b: parseInt(swapB) })
            .then(() => {
                alert('Topics swapped successfully!');
                api.get('/topics').then(res => setTopics(res.data));
            })
            .catch(err => {
                console.error('Swap failed:', err);
                alert('Swap failed! Check console for details.');
            });
    };

    // Modal for uploading a topic document
    const [docFile, setDocFile] = useState(null);
    const [uploadingTopicId, setUploadingTopicId] = useState(null);
    const [docTitle, setDocTitle] = useState('');
    const [docContent, setDocContent] = useState('');
    const [docNote, setDocNote] = useState('');

    const handleUploadDoc = async () => {
        if (!docTitle.trim() && !docFile) {
            alert('Please provide a document title or upload a file.');
            return;
        }

        const formData = new FormData();
        formData.append('topic_id', uploadingTopicId);
        formData.append('title', docTitle);
        formData.append('note', docNote);
        if (docContent.trim()) {
            formData.append('text', docContent); // optional pasted input
        }
        if (docFile) {
            formData.append('file', docFile); // actual file
        }

        try {
            await api.post('/topic-updates/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            alert('Document uploaded and processed successfully.');
            setUploadingTopicId(null);
            setDocTitle('');
            setDocContent('');
            setDocNote('');
            setDocFile(null);
        } catch (err) {
            console.error('Upload failed:', err);
            alert('Upload failed. See console for details.');
        }
    };

    const [summaryTopicId, setSummaryTopicId] = useState(null);
    const [summaryDate, setSummaryDate] = useState('');
    const [summaryNote, setSummaryNote] = useState('');

    const handleGenerateSummary = async () => {
        if (!summaryDate) {
            alert('Please select a summary date.');
            return;
        }

        try {
            const res = await api.post(`/topics/summary/${summaryTopicId}`, {
                summary_date: summaryDate,
                note: summaryNote
            });

            alert('Topic summary created!');
            setSummaryTopicId(null);
            setSummaryDate('');
            setSummaryNote('');
        } catch (err) {
            console.error('Summary error:', err);
            alert('Summary generation failed. See console.');
        }
    };

    const [timelineTopicId, setTimelineTopicId] = useState(null);

    // Fetch timeline when a topic is selected for timeline view
    useEffect(() => {
        if (timelineTopicId) {
            setLoadingTimeline(true);
            api.get(`/topics/${timelineTopicId}/timeline`)
                .then(res => setTimelineItems(res.data))
                .finally(() => setLoadingTimeline(false));
        }
    }, [timelineTopicId]);

    const handleRenameClick = (id) => {
        setPendingRenameId(id);
        setPendingNewName(newName);
        setShowDeletePrompt(true);
    };

    const handleRenameConfirm = (deleteChats) => {
        renameTopic(pendingRenameId, pendingNewName, deleteChats);
        setShowDeletePrompt(false);
        setPendingRenameId(null);
        setPendingNewName('');
    };

    const handleView = (type, item) => {
        setViewerItem(item);
        setViewerType(type);
        setViewerContent(type === 'summary' ? (item.summary || item.preview) : (item.full || ''));
    };

    const handleRemove = (item) => setRemoveItem(item);

    const handleSignificance = async (item, delta) => {
        const current = item.significance || 1;
        const newScore = Math.max(0, Math.min(5, current + delta));
        // Only show remove warning if decreasing from 1 to 0
        if (delta < 0 && current === 1 && newScore === 0) {
            setRemoveItem(item);
            return;
        }
        try {
            if (item.type === 'chat') {
                const topicType = item.primary ? 'primary' : 'secondary';
                await api.patch(`/topics/timeline/chat/${item.id}/significance`, { topicType, value: newScore });
                setTimelineItems(items => items.map(it => it.id === item.id ? { ...it, significance: newScore } : it));
            } else {
                await api.patch(`/topics/timeline/${item.id}/significance`, { value: newScore });
                setTimelineItems(items => items.map(it => it.id === item.id ? { ...it, significance: newScore } : it));
            }
        } catch (err) {
            alert('Failed to update significance.');
        }
    };

    const confirmRemove = async (item) => {
        try {
            if (item.type === 'chat') {
                const topicType = item.primary ? 'primary' : 'secondary';
                await api.post(`/topics/timeline/chat/${item.id}/exclude`, { topicType });
            } else {
                await api.patch(`/topics/timeline/${item.id}/significance`, { value: 0 });
            }
            setTimelineItems(items => items.filter(it => it.id !== item.id || (item.type === 'chat' && (item.primary ? it.significance !== 0 : it.significance !== 0))));
            setRemoveItem(null);
        } catch (err) {
            alert('Failed to remove from timeline.');
        }
    };

    const handleFlip = (id) => {
        setFlippedCardId(flippedCardId === id ? null : id);
    };

    // Helper for significance color
    const getSigColor = (score) => {
        switch (score) {
            case 5: return 'border-white text-white';
            case 4: return 'border-gray-200 text-gray-200';
            case 3: return 'border-yellow-300 text-yellow-200';
            case 2: return 'border-green-300 text-green-200';
            default: return 'border-green-600 text-green-300';
        }
    };

    const getTypeIcon = (type) => {
        if (type === 'chat') return <ChatBubbleLeftRightIcon className="h-6 w-6 mx-auto" />;
        if (type === 'document') return <DocumentTextIcon className="h-6 w-6 mx-auto" />;
        return <SparklesIcon className="h-6 w-6 mx-auto" />;
    };

    return (
        <div className="terminal min-h-screen p-6 font-mono text-green-300 relative">
            {/* Star Map Grid Layout */}
            <div className="relative z-10">
                <h2 className="text-green-400 text-xl mb-4 text-center">TOPIC MATRIX</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-black border border-green-700 rounded p-4 text-green-300 text-sm font-mono shadow-md">
                        <span className="font-bold text-green-400">1. Timeline Navigation:</span> Scroll horizontally to explore topic events. Cards are color-coded by type and significance.
                    </div>
                    <div className="bg-black border border-green-700 rounded p-4 text-green-300 text-sm font-mono shadow-md">
                        <span className="font-bold text-green-400">2. Card Actions:</span> Use the buttons to view summaries, full content, or adjust significance. Setting a score to zero will remove the item from the timeline (not from exports).
                    </div>
                    <div className="bg-black border border-green-700 rounded p-4 text-green-300 text-sm font-mono shadow-md">
                        <span className="font-bold text-green-400">3. Management:</span> Chats removed from the timeline remain accessible in the Chats page. Use the close button to exit the timeline view.
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-5 sm:[grid-template-rows:80px_80px_auto_80px] gap-4 w-full mx-auto mb-4 pb-8">
                    {/* T1: col 3, row 1 */}
                    <div className="col-span-1 w-full sm:col-start-3 sm:row-start-1 min-w-[280px] p-3">
                        {topics[0] && (
                            <div className="border border-green-700 rounded bg-black shadow-sm space-y-2 hover:shadow-lg transition p-3">
                                <div className="text-center">
                                    <div className="text-green-400 font-bold uppercase text-xs">{numberWords[topics[0].topic_id-1]}</div>
                                    <div className="text-green-400 font-bold text-base">{topics[0].topic_name}</div>
                                </div>
                                <div className="flex flex-row gap-2 justify-center">
                                    <button title="Rename Topic" className="btn-icon" onClick={() => setSelectedTopic(topics[0].topic_id)}><Pencil size={20} /></button>
                                    <button title="Upload Document" className="btn-icon" onClick={() => setUploadingTopicId(topics[0].topic_id)}><Upload size={20} /></button>
                                    <button title="Generate Summary" className="btn-icon" onClick={() => setSummaryTopicId(topics[0].topic_id)}><Brain size={20} /></button>
                                    <button title="Open Timeline" className="btn-icon" onClick={() => setTimelineTopicId(topics[0].topic_id)}><Clock size={20} /></button>
                                </div>
                                {selectedTopic === topics[0].topic_id && (
                                    <div className="mt-2">
                                        <input
                                            className="input-terminal mb-2"
                                            value={newName}
                                            onChange={e => setNewName(e.target.value)}
                                            placeholder="New topic name"
                                        />
                                        <div className="flex gap-2">
                                            <button onClick={() => handleRenameClick(topics[0].topic_id)} className="btn-primary">Submit</button>
                                            <button onClick={() => setSelectedTopic(null)} className="btn-primary">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* T5: col 1, row 2 */}
                    <div className="col-span-1 w-full sm:col-start-1 sm:row-start-2 min-w-[280px] p-3">
                        {topics[4] && (
                            <div className="border border-green-700 rounded bg-black shadow-sm space-y-2 hover:shadow-lg transition p-3">
                                <div className="text-center">
                                    <div className="text-green-400 font-bold uppercase text-xs">{numberWords[topics[4].topic_id-1]}</div>
                                    <div className="text-green-400 font-bold text-base">{topics[4].topic_name}</div>
                                </div>
                                <div className="flex flex-row gap-2 justify-center">
                                    <button title="Rename Topic" className="btn-icon" onClick={() => setSelectedTopic(topics[4].topic_id)}><Pencil size={20} /></button>
                                    <button title="Upload Document" className="btn-icon" onClick={() => setUploadingTopicId(topics[4].topic_id)}><Upload size={20} /></button>
                                    <button title="Generate Summary" className="btn-icon" onClick={() => setSummaryTopicId(topics[4].topic_id)}><Brain size={20} /></button>
                                    <button title="Open Timeline" className="btn-icon" onClick={() => setTimelineTopicId(topics[4].topic_id)}><Clock size={20} /></button>
                                </div>
                                {selectedTopic === topics[4].topic_id && (
                                    <div className="mt-2">
                                        <input
                                            className="input-terminal mb-2"
                                            value={newName}
                                            onChange={e => setNewName(e.target.value)}
                                            placeholder="New topic name"
                                        />
                                        <div className="flex gap-2">
                                            <button onClick={() => handleRenameClick(topics[4].topic_id)} className="btn-primary">Submit</button>
                                            <button onClick={() => setSelectedTopic(null)} className="btn-primary">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* T2: col 5, row 2 */}
                    <div className="col-span-1 w-full sm:col-start-5 sm:row-start-2 min-w-[280px] p-3">
                        {topics[1] && (
                            <div className="border border-green-700 rounded bg-black shadow-sm space-y-2 hover:shadow-lg transition p-3">
                                <div className="text-center">
                                    <div className="text-green-400 font-bold uppercase text-xs">{numberWords[topics[1].topic_id-1]}</div>
                                    <div className="text-green-400 font-bold text-base">{topics[1].topic_name}</div>
                                </div>
                                <div className="flex flex-row gap-2 justify-center">
                                    <button title="Rename Topic" className="btn-icon" onClick={() => setSelectedTopic(topics[1].topic_id)}><Pencil size={20} /></button>
                                    <button title="Upload Document" className="btn-icon" onClick={() => setUploadingTopicId(topics[1].topic_id)}><Upload size={20} /></button>
                                    <button title="Generate Summary" className="btn-icon" onClick={() => setSummaryTopicId(topics[1].topic_id)}><Brain size={20} /></button>
                                    <button title="Open Timeline" className="btn-icon" onClick={() => setTimelineTopicId(topics[1].topic_id)}><Clock size={20} /></button>
                                </div>
                                {selectedTopic === topics[1].topic_id && (
                                    <div className="mt-2">
                                        <input
                                            className="input-terminal mb-2"
                                            value={newName}
                                            onChange={e => setNewName(e.target.value)}
                                            placeholder="New topic name"
                                        />
                                        <div className="flex gap-2">
                                            <button onClick={() => handleRenameClick(topics[1].topic_id)} className="btn-primary">Submit</button>
                                            <button onClick={() => setSelectedTopic(null)} className="btn-primary">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* SWAP: col 3, row 3 */}
                    <div className="col-span-1 w-full order-first sm:order-none sm:col-start-3 sm:row-start-3 flex items-center justify-center min-w-[280px] p-3">
                        <div className="bg-black border border-green-700 rounded p-4 flex flex-col items-center w-full max-w-xs shadow-lg">
                            <h3 className="text-green-400 text-lg mb-2">Swap Positions</h3>
                            <select value={swapA} onChange={e => setSwapA(e.target.value)} className="input-terminal w-40 mb-2">
                                <option value="">Select Topic A</option>
                                {topics.map(t => <option key={t.topic_id} value={t.topic_id}>{t.topic_name}</option>)}
                            </select>
                            <span className="text-green-300 mb-2">with</span>
                            <select value={swapB} onChange={e => setSwapB(e.target.value)} className="input-terminal w-40 mb-4">
                                <option value="">Select Topic B</option>
                                {topics.map(t => <option key={t.topic_id} value={t.topic_id}>{t.topic_name}</option>)}
                            </select>
                            <button onClick={swapTopics} className="btn-primary w-full">Swap</button>
                        </div>
                    </div>
                    {/* T4: col 2, row 4 */}
                    <div className="col-span-1 w-full sm:col-start-2 sm:row-start-4 min-w-[280px] p-3">
                        {topics[3] && (
                            <div className="border border-green-700 rounded bg-black shadow-sm space-y-2 hover:shadow-lg transition p-3">
                                <div className="text-center">
                                    <div className="text-green-400 font-bold uppercase text-xs">{numberWords[topics[3].topic_id-1]}</div>
                                    <div className="text-green-400 font-bold text-base">{topics[3].topic_name}</div>
                                </div>
                                <div className="flex flex-row gap-2 justify-center">
                                    <button title="Rename Topic" className="btn-icon" onClick={() => setSelectedTopic(topics[3].topic_id)}><Pencil size={20} /></button>
                                    <button title="Upload Document" className="btn-icon" onClick={() => setUploadingTopicId(topics[3].topic_id)}><Upload size={20} /></button>
                                    <button title="Generate Summary" className="btn-icon" onClick={() => setSummaryTopicId(topics[3].topic_id)}><Brain size={20} /></button>
                                    <button title="Open Timeline" className="btn-icon" onClick={() => setTimelineTopicId(topics[3].topic_id)}><Clock size={20} /></button>
                                </div>
                                {selectedTopic === topics[3].topic_id && (
                                    <div className="mt-2">
                                        <input
                                            className="input-terminal mb-2"
                                            value={newName}
                                            onChange={e => setNewName(e.target.value)}
                                            placeholder="New topic name"
                                        />
                                        <div className="flex gap-2">
                                            <button onClick={() => handleRenameClick(topics[3].topic_id)} className="btn-primary">Submit</button>
                                            <button onClick={() => setSelectedTopic(null)} className="btn-primary">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* T3: col 4, row 4 */}
                    <div className="col-span-1 w-full sm:col-start-4 sm:row-start-4 min-w-[280px] p-3">
                        {topics[2] && (
                            <div className="border border-green-700 rounded bg-black shadow-sm space-y-2 hover:shadow-lg transition p-3">
                                <div className="text-center">
                                    <div className="text-green-400 font-bold uppercase text-xs">{numberWords[topics[2].topic_id-1]}</div>
                                    <div className="text-green-400 font-bold text-base">{topics[2].topic_name}</div>
                                </div>
                                <div className="flex flex-row gap-2 justify-center">
                                    <button title="Rename Topic" className="btn-icon" onClick={() => setSelectedTopic(topics[2].topic_id)}><Pencil size={20} /></button>
                                    <button title="Upload Document" className="btn-icon" onClick={() => setUploadingTopicId(topics[2].topic_id)}><Upload size={20} /></button>
                                    <button title="Generate Summary" className="btn-icon" onClick={() => setSummaryTopicId(topics[2].topic_id)}><Brain size={20} /></button>
                                    <button title="Open Timeline" className="btn-icon" onClick={() => setTimelineTopicId(topics[2].topic_id)}><Clock size={20} /></button>
                                </div>
                                {selectedTopic === topics[2].topic_id && (
                                    <div className="mt-2">
                                        <input
                                            className="input-terminal mb-2"
                                            value={newName}
                                            onChange={e => setNewName(e.target.value)}
                                            placeholder="New topic name"
                                        />
                                        <div className="flex gap-2">
                                            <button onClick={() => handleRenameClick(topics[2].topic_id)} className="btn-primary">Submit</button>
                                            <button onClick={() => setSelectedTopic(null)} className="btn-primary">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Upload Doc Modal */}
                {uploadingTopicId && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                        <div className="bg-[#0a0a0a] border border-green-500 rounded p-6 w-full max-w-2xl shadow-lg text-green-300 font-mono">
                            <h3 className="text-green-400 text-xl mb-4">Upload Topic Document (T{uploadingTopicId})</h3>
                            <input
                                className="input-terminal mb-3"
                                placeholder="Document Title"
                                value={docTitle}
                                onChange={(e) => setDocTitle(e.target.value)}
                            />
                            <textarea
                                className="input-terminal mb-3"
                                placeholder="Paste or type full document here, OR upload a file below (one or the other)."
                                value={docContent}
                                onChange={(e) => setDocContent(e.target.value)}
                                rows={10}
                            />
                            <textarea
                                className="input-terminal mb-4"
                                placeholder="Optional note or context for this document..."
                                value={docNote}
                                onChange={(e) => setDocNote(e.target.value)}
                                rows={3}
                            />
                            <input
                                type="file"
                                onChange={(e) => setDocFile(e.target.files[0])}
                                className="input-terminal mb-3"
                            />
                            <div className="flex justify-between gap-4">
                                <button onClick={() => setUploadingTopicId(null)} className="btn-primary w-full">Cancel</button>
                                <button onClick={handleUploadDoc} className="btn-primary w-full">Upload</button>
                            </div>
                        </div>
                    </div>
                )}

                {summaryTopicId && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                        <div className="bg-[#0a0a0a] border border-green-500 rounded p-6 w-full max-w-md shadow-lg text-green-300 font-mono">
                            <h3 className="text-green-400 text-xl mb-4">Generate Summary (T{summaryTopicId})</h3>

                            <label className="block mb-2 text-sm text-green-400">Summary Date:</label>
                            <input
                                type="date"
                                className="input-terminal mb-4"
                                value={summaryDate}
                                onChange={(e) => setSummaryDate(e.target.value)}
                            />

                            <label className="block mb-2 text-sm text-green-400">Optional Note:</label>
                            <textarea
                                rows={3}
                                className="input-terminal mb-4"
                                value={summaryNote}
                                onChange={(e) => setSummaryNote(e.target.value)}
                                placeholder="e.g. Catching up after October uploads"
                            />

                            <div className="flex justify-between gap-4">
                                <button onClick={() => setSummaryTopicId(null)} className="btn-primary w-full">Cancel</button>
                                <button onClick={handleGenerateSummary} className="btn-primary w-full">Generate</button>
                            </div>
                        </div>
                    </div>
                )}

                <hr className="my-6 border-green-800" />

                {timelineTopicId && (
                    <div className="relative w-full mt-2 mb-8">
                        <div className="flex justify-end mb-2">
                            <button
                                className="btn-primary"
                                onClick={() => setTimelineTopicId(null)}
                            >
                                Close Timeline
                            </button>
                        </div>
                        <div className="overflow-x-auto whitespace-nowrap px-2 pb-6 relative" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <div className="flex gap-8 relative" style={{ minHeight: 180 }}>
                                {timelineItems.map((item, i) => {
                                    const isFlipped = flippedCardId === item.id;
                                    return (
                                        <div
                                            key={item.id}
                                            className="inline-block align-bottom relative transition-transform duration-200"
                                            style={{ perspective: '1000px' }}
                                        >
                                            <div
                                                className={`relative w-48 h-56 cursor-pointer [transform-style:preserve-3d] transition-transform duration-500 ${isFlipped ? '[transform:rotateY(180deg)]' : ''}`}
                                                onClick={() => handleFlip(item.id)}
                                            >
                                                {/* Front */}
                                                <div
                                                    className={`absolute w-full h-full rounded-lg bg-black border shadow-md flex flex-col items-center justify-start pt-4 ${getSigColor(item.significance)} [backface-visibility:hidden]`}
                                                >
                                                    <div className="font-bold text-base mb-1 text-center truncate w-full px-2 uppercase" title={item.title}>{item.title}</div>
                                                    <div className="text-xs mb-4 text-center">
                                                        {(() => {
                                                            const d = new Date(item.date);
                                                            const day = d.getDate();
                                                            const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
                                                            const year = String(d.getFullYear()).slice(-2);
                                                            return `${day} ${month} ${year}`;
                                                        })()}
                                                    </div>
                                                    <div className="mb-2">{getTypeIcon(item.type)}</div>
                                                </div>
                                                {/* Back */}
                                                <div className={`absolute w-full h-full rounded-lg bg-black border shadow-md flex flex-col items-center justify-center gap-2 ${getSigColor(item.significance)} [transform:rotateY(180deg)] [backface-visibility:hidden]`}
                                                >
                                                    <div className="font-bold text-base mb-1">Manage</div>
                                                    <div className="text-xs mb-2">Score: {item.significance}</div>
                                                    <div className="flex gap-2 mb-2">
                                                        <button className="btn-icon" title="Show Summary" onClick={e => { e.stopPropagation(); handleView('summary', item); }}>
                                                            <span>Σ</span>
                                                        </button>
                                                        <button className="btn-icon" title="Show Full" onClick={e => { e.stopPropagation(); handleView('full', item); }}>
                                                            <span>≡</span>
                                                        </button>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button className="btn-icon" title="Increase Score" onClick={e => { e.stopPropagation(); handleSignificance(item, 1); }}>
                                                            <span>+</span>
                                                        </button>
                                                        <button className="btn-icon" title="Decrease/Remove" onClick={e => { e.stopPropagation(); handleRemove(item); }}>
                                                            <span>-</span>
                                                        </button>
                                                    </div>
                                                    <div className="text-xs opacity-60 mt-2">Click to flip back</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {loadingTimeline && <div className="text-center text-green-400 mt-4">Loading timeline...</div>}
                        {viewerItem && (
                            <div className="mt-6 bg-black border border-green-700 rounded p-6 text-green-200 font-mono max-w-2xl mx-auto">
                                <div className="flex justify-between items-center mb-2">
                                    <div className="font-bold text-green-400">{viewerType === 'summary' ? 'Summary' : 'Full Content'}</div>
                                    <button className="btn-secondary" onClick={() => setViewerItem(null)}>Close</button>
                                </div>
                                <div className="whitespace-pre-wrap text-sm">{viewerContent}</div>
                            </div>
                        )}
                        {removeItem && (
                            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                                <div className="bg-[#0a0a0a] border border-green-500 rounded p-6 w-full max-w-md shadow-lg text-green-300 font-mono">
                                    <h3 className="text-green-400 text-xl mb-4">Remove from Timeline?</h3>
                                    <p className="mb-4">Setting a score of zero will remove this item from the topic timeline but not delete or exclude it from Injector and Archiver exports. {removeItem.type === 'chat' ? 'If this item is a chat, you will be able to view it from the Chats page.' : ''} Do you wish to continue and give this card a score of zero?</p>
                                    <div className="flex gap-4">
                                        <button onClick={() => confirmRemove(removeItem)} className="btn-primary w-full">Yes, Remove</button>
                                        <button onClick={() => setRemoveItem(null)} className="btn-secondary w-full">Cancel</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {showDeletePrompt && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                        <div className="bg-[#0a0a0a] border border-green-500 rounded p-6 w-full max-w-md shadow-lg text-green-300 font-mono">
                            <h3 className="text-green-400 text-xl mb-4">Rename Topic</h3>
                            <p className="mb-4">Do you want to delete all chats and topic updates associated with this topic, or just rename it?</p>
                            <div className="flex gap-4">
                                <button onClick={() => handleRenameConfirm(false)} className="btn-primary w-full">Just Rename</button>
                                <button onClick={() => handleRenameConfirm(true)} className="bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-4 rounded w-full">Rename & Delete</button>
                            </div>
                            <button onClick={() => setShowDeletePrompt(false)} className="btn-primary w-full mt-4">Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Topics;